import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Ban, CheckSquare, Copy, FileText, Loader2, Paperclip, Send, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

const DAY_MS = 24 * 3600 * 1000;
const istDay = (iso) => new Date(iso).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const timeOf = (iso) => new Date(iso).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: true });
function dayLabel(iso) {
  const d = istDay(iso);
  const today = istDay(new Date().toISOString());
  const yest = istDay(new Date(Date.now() - DAY_MS).toISOString());
  if (d === today) return "Aaj";
  if (d === yest) return "Kal";
  return new Date(iso).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "numeric", month: "short", year: "numeric" });
}

export function AgencyEkycChat({ groupId }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [menu, setMenu] = useState(null);
  const bottomRef = useRef(null);
  const lastIdRef = useRef("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/agency-ekyc/chat/${groupId}/messages`);
      setMsgs(data.messages);
      const last = data.messages[data.messages.length - 1]?.id || "";
      if (last !== lastIdRef.current) {
        lastIdRef.current = last;
        setTimeout(() => bottomRef.current?.scrollIntoView({ block: "end" }), 50);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [groupId]);
  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  const canDelete = (m) => !m.deleted && (user.role === "admin" || (m.sender_id === user.id && Date.now() - new Date(m.created_at).getTime() < DAY_MS));
  const selMsgs = (msgs || []).filter((m) => selected.has(m.id));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const del = async (ids) => {
    if (!window.confirm(`${ids.length} message delete karein? (sabke liye)`)) return;
    try {
      await api.post(`/agency-ekyc/chat/${groupId}/delete`, { ids });
      setSelected(new Set());
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const copy = (list) => navigator.clipboard.writeText(list.map((m) => m.text).filter(Boolean).join("\n")).then(() => toast.success("Copied")).catch(() => {});

  return (
    <div className="flex flex-col border border-[#EDEDE5] rounded-xl overflow-hidden bg-white h-[calc(100vh-220px)] min-h-[420px]" data-testid="agency-ekyc-chat">
      {selected.size > 0 && (
        <div className="px-3 py-2.5 bg-[#385C4B] text-white flex items-center gap-3" data-testid="ekyc-chat-select-bar">
          <button onClick={() => setSelected(new Set())} data-testid="ekyc-chat-select-cancel"><X className="w-5 h-5" /></button>
          <span className="font-bold">{selected.size} selected</span>
          <button onClick={() => copy(selMsgs)} className="ml-auto p-1.5 hover:bg-white/10 rounded" title="Copy" data-testid="ekyc-chat-select-copy"><Copy className="w-4 h-4" /></button>
          <button onClick={() => del([...selected])} disabled={!selMsgs.every(canDelete)}
            className="p-1.5 hover:bg-white/10 rounded disabled:opacity-40" data-testid="ekyc-chat-select-delete"><Trash2 className="w-4 h-4" /></button>
        </div>
      )}
      <div className="flex-1 overflow-y-auto px-2 sm:px-4 py-3 bg-[#EFEAE2]" data-testid="ekyc-chat-messages">
        {msgs === null ? <div className="text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
          : msgs.length === 0 ? <div className="text-center text-xs text-[#6B6B61] mt-10">Abhi koi message nahi. Pehla message ya document bhejo 👋</div>
          : msgs.map((m, i) => (
            <React.Fragment key={m.id}>
              {(i === 0 || istDay(msgs[i - 1].created_at) !== istDay(m.created_at)) && (
                <div className="text-center my-2"><span className="text-[11px] bg-white/90 text-[#6B6B61] px-2.5 py-1 rounded-md shadow-sm">{dayLabel(m.created_at)}</span></div>
              )}
              <Bubble m={m} mine={m.sender_id === user.id} selected={selected.has(m.id)} selecting={selected.size > 0}
                onToggle={() => toggle(m.id)} onMenu={(e) => { e.preventDefault(); setMenu({ x: e.clientX, y: e.clientY, m }); }} />
            </React.Fragment>
          ))}
        <div ref={bottomRef} />
      </div>
      <Composer groupId={groupId} onSent={load} />
      {menu && (
        <BubbleMenu anchor={menu} onClose={() => setMenu(null)} onSelect={() => toggle(menu.m.id)}
          onCopy={menu.m.text ? () => copy([menu.m]) : null}
          onDelete={canDelete(menu.m) ? () => del([menu.m.id]) : null} />
      )}
    </div>
  );
}

function Bubble({ m, mine, selected, selecting, onToggle, onMenu }) {
  const longPress = useRef(null);
  const touchStart = (e) => {
    const t = e.touches[0];
    longPress.current = setTimeout(() => onMenu({ preventDefault() {}, clientX: t.clientX, clientY: t.clientY }), 500);
  };
  const touchEnd = () => clearTimeout(longPress.current);
  return (
    <div onClick={selecting ? onToggle : undefined} onContextMenu={onMenu} onTouchStart={touchStart} onTouchEnd={touchEnd} onTouchMove={touchEnd}
      className={`flex my-1 ${mine ? "justify-end" : "justify-start"} ${selected ? "bg-[#385C4B]/15 -mx-2 px-2 sm:-mx-4 sm:px-4" : ""} ${selecting ? "cursor-pointer" : ""}`}
      data-testid={`ekyc-chat-msg-${m.id}`}>
      <div className={`max-w-[85%] sm:max-w-[65%] rounded-lg px-2.5 py-1.5 shadow-sm ${mine ? "bg-[#D9FDD3]" : "bg-white"}`}>
        {!mine && <div className="text-[11px] font-bold text-[#2F6F8F]">{m.sender_name}{m.sender_role === "admin" ? " · Admin" : ""}</div>}
        {m.deleted ? (
          <div className="text-sm italic text-[#6B6B61] flex items-center gap-1"><Ban className="w-3.5 h-3.5" />Ye message delete kar diya gaya</div>
        ) : (
          <>
            {m.attachments?.map((a) => <Attachment key={a.file_id} a={a} />)}
            {m.text && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
          </>
        )}
        <div className="text-[10px] text-[#6B6B61] text-right mt-0.5">{timeOf(m.created_at)}</div>
      </div>
    </div>
  );
}

function useBlobUrl(fileId) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let u = "";
    api.get(`/agency-ekyc/files/${fileId}`, { responseType: "blob" }).then((res) => { u = URL.createObjectURL(res.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [fileId]);
  return url;
}

function Attachment({ a }) {
  const isImg = (a.content_type || "").startsWith("image/");
  const url = useBlobUrl(a.file_id);
  if (isImg) {
    return url ? (
      <a href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
        <img src={url} alt={a.name} className="rounded max-h-64 max-w-full mb-1 object-contain" data-testid={`ekyc-chat-img-${a.file_id}`} />
      </a>
    ) : <div className="w-40 h-24 bg-black/5 rounded mb-1 animate-pulse" />;
  }
  return (
    <a href={url || undefined} download={a.name} onClick={(e) => e.stopPropagation()} className="flex items-center gap-2 bg-black/5 rounded px-2 py-1.5 mb-1 text-xs" data-testid={`ekyc-chat-file-${a.file_id}`}>
      <FileText className="w-5 h-5 text-[#C8553D]" />
      <span className="truncate max-w-[180px]">{a.name}</span>
      <span className="text-[#6B6B61]">{Math.ceil(a.size / 1024)} KB</span>
    </a>
  );
}

function Composer({ groupId, onSent }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const pick = async (e) => {
    const list = [...(e.target.files || [])];
    e.target.value = "";
    setBusy(true);
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post(`/agency-ekyc/chat/${groupId}/upload`, fd);
        setFiles((x) => [...x, data]);
      } catch (err) {
        toast.error(`${f.name}: ${formatApiError(err)}`);
      }
    }
    setBusy(false);
  };
  const send = async () => {
    if (!text.trim() && files.length === 0) return;
    setBusy(true);
    try {
      await api.post(`/agency-ekyc/chat/${groupId}/messages`, { text, attachments: files });
      setText("");
      setFiles([]);
      onSent();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border-t border-[#EDEDE5] p-2 bg-[#F5F5F0]">
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="ekyc-chat-pending-files">
          {files.map((f) => (
            <span key={f.file_id} className="text-xs bg-white border border-[#DCDCD3] rounded px-2 py-1 flex items-center gap-1">
              📎 {f.name}<button onClick={() => setFiles((x) => x.filter((y) => y.file_id !== f.file_id))}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <button onClick={() => fileRef.current?.click()} className="p-2 rounded-full hover:bg-black/5" title="Photo / Document" data-testid="ekyc-chat-attach">
          <Paperclip className="w-5 h-5 text-[#6B6B61]" />
        </button>
        <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.csv,.txt,.xlsx,.xls" className="hidden" onChange={pick} data-testid="ekyc-chat-file-input" />
        <textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          className="flex-1 resize-none rounded-2xl border border-[#DCDCD3] px-3 py-2 text-sm max-h-32 bg-white" data-testid="ekyc-chat-input" />
        <button onClick={send} disabled={busy} className="p-2.5 rounded-full bg-[#385C4B] text-white disabled:opacity-50" data-testid="ekyc-chat-send">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function BubbleMenu({ anchor, onClose, onSelect, onCopy, onDelete }) {
  const style = useMemo(() => ({
    left: Math.min(anchor.x, window.innerWidth - 190), top: Math.min(anchor.y, window.innerHeight - 160),
  }), [anchor]);
  const item = (Icon, label, fn, testid, red) => fn && (
    <button onClick={() => { fn(); onClose(); }} data-testid={testid}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-[#F5F5F0] ${red ? "text-[#C8553D]" : ""}`}>
      <Icon className="w-4 h-4" />{label}
    </button>
  );
  return createPortal(
    <>
      <div className="fixed inset-0 z-[100]" onMouseDown={onClose} onTouchStart={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} />
      <div className="fixed z-[101] bg-white border border-[#DCDCD3] shadow-2xl rounded-xl py-1 w-[180px]" style={style} data-testid="ekyc-chat-context-menu">
        {item(CheckSquare, "Select", onSelect, "ekyc-ctx-select")}
        {item(Copy, "Copy", onCopy, "ekyc-ctx-copy")}
        {item(Trash2, "Delete for everyone", onDelete, "ekyc-ctx-delete", true)}
      </div>
    </>,
    document.body,
  );
}
